// FORTE product catalog for the in-portal Shop + "Suggested for you" bar.
// Static for now — when Bask headless API access lands, swap this for a
// products endpoint. Compliance: Rx products NEVER add-to-cart; they route
// to the vertical's medical assessment (or the waitlist if not yet live).

import type { TreatmentRow, AssessmentRow } from "@/lib/database.types";

export type Vertical = "weight-loss" | "trt" | "ed" | "hair" | "longevity";

export interface ShopProduct {
  id: string;
  vertical: Vertical;
  name: string;
  tagline: string;
  detail: string;
  priceLabel: string;
  /** Rx products route to an assessment; false = waitlist CTA. */
  available: boolean;
  assessmentUrl: string | null;
}

const WL_INTAKE = "https://intake.fortehealth.co";
const TRT_INTAKE = "https://trt.fortehealth.co";
const WAITLIST = "https://fortehealth.co/get-started";

export const CATALOG: ShopProduct[] = [
  // Weight loss ------------------------------------------------------------
  {
    id: "wl-semaglutide",
    vertical: "weight-loss",
    name: "Semaglutide",
    tagline: "Compounded GLP-1 · weekly injection",
    detail: "Clinician-prescribed GLP-1 therapy titrated to your goals, with check-ins along the way.",
    priceLabel: "from $179/mo",
    available: true,
    assessmentUrl: WL_INTAKE,
  },
  {
    id: "wl-tirzepatide",
    vertical: "weight-loss",
    name: "Tirzepatide",
    tagline: "Compounded GLP-1/GIP · weekly injection",
    detail: "Dual-agonist therapy for patients who want the strongest clinical results.",
    priceLabel: "from $279/mo",
    available: true,
    assessmentUrl: WL_INTAKE,
  },
  {
    id: "wl-oral",
    vertical: "weight-loss",
    name: "Oral GLP-1",
    tagline: "Compounded semaglutide or tirzepatide · daily troche",
    detail: "Needle-free GLP-1 in a daily rapid-dissolve troche.",
    priceLabel: "from $149/mo",
    available: true,
    assessmentUrl: WL_INTAKE,
  },
  // Testosterone -----------------------------------------------------------
  {
    id: "trt-enclomiphene",
    vertical: "trt",
    name: "Enclomiphene",
    tagline: "Oral testosterone support",
    detail: "Supports your body's own testosterone production — fertility-friendly.",
    priceLabel: "from $89/mo",
    available: true,
    assessmentUrl: TRT_INTAKE,
  },
  {
    id: "trt-injectable",
    vertical: "trt",
    name: "Injectable TRT",
    tagline: "Testosterone cypionate · weekly",
    detail: "Classic testosterone replacement, clinician-monitored with labs.",
    priceLabel: "launching soon",
    available: false,
    assessmentUrl: null,
  },
  // ED / sexual health -------------------------------------------------------
  {
    id: "ed-edge",
    vertical: "ed",
    name: "FORTE EDGE",
    tagline: "4-in-1 rapid-dissolve troche",
    detail: "Sildenafil + tadalafil + apomorphine + oxytocin in one fast-acting troche.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
  {
    id: "ed-tadalafil",
    vertical: "ed",
    name: "Tadalafil",
    tagline: "Daily or as-needed",
    detail: "Long-window support so timing is never the issue.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
  // Hair -------------------------------------------------------------------
  {
    id: "hair-topical",
    vertical: "hair",
    name: "Hair Restore 5-in-1",
    tagline: "Topical finasteride + minoxidil formula",
    detail: "Five actives in one nightly topical — no pills required.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
  // Longevity --------------------------------------------------------------
  {
    id: "lg-nad",
    vertical: "longevity",
    name: "NAD+",
    tagline: "Cellular energy · subcutaneous",
    detail: "Supports energy, recovery, and healthy aging alongside your plan.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
  {
    id: "lg-sermorelin",
    vertical: "longevity",
    name: "Sermorelin",
    tagline: "Growth-hormone peptide support",
    detail: "Peptide therapy for recovery, sleep quality, and body composition.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
  {
    id: "lg-glutathione",
    vertical: "longevity",
    name: "Glutathione",
    tagline: "Master antioxidant",
    detail: "Antioxidant support for recovery and overall wellness.",
    priceLabel: "coming soon",
    available: false,
    assessmentUrl: null,
  },
];

export const WAITLIST_URL = WAITLIST;

export const VERTICAL_LABEL: Record<Vertical, string> = {
  "weight-loss": "Weight Loss",
  trt: "Testosterone",
  ed: "Sexual Health",
  hair: "Hair",
  longevity: "Longevity",
};

// ---------------------------------------------------------------------------
// "Suggested for you" — rule-based cross-sell.
// Owned verticals (active/pending treatments) are excluded; each owned
// vertical promotes complementary ones. Goals from the WL intake nudge
// ordering (e.g. "energy" → longevity first).
// ---------------------------------------------------------------------------

function verticalOfMedication(medication: string): Vertical | null {
  const med = medication.toLowerCase();
  if (med.includes("semaglutide") || med.includes("tirzepatide") || med.includes("glp")) return "weight-loss";
  if (med.includes("testosterone") || med.includes("enclomiphene")) return "trt";
  if (med.includes("sildenafil") || med.includes("tadalafil") || med.includes("edge")) return "ed";
  if (med.includes("finasteride") || med.includes("minoxidil")) return "hair";
  if (med.includes("nad") || med.includes("sermorelin") || med.includes("glutathione")) return "longevity";
  return null;
}

// What complements what (ordered by relevance for that patient profile).
const COMPLEMENTS: Record<Vertical, Vertical[]> = {
  "weight-loss": ["longevity", "trt", "ed", "hair"],
  trt: ["ed", "longevity", "hair", "weight-loss"],
  ed: ["trt", "longevity", "weight-loss", "hair"],
  hair: ["trt", "longevity", "ed", "weight-loss"],
  longevity: ["weight-loss", "trt", "ed", "hair"],
};

export function suggestedProducts(
  treatments: TreatmentRow[],
  assessments: AssessmentRow[],
  limit = 3,
): ShopProduct[] {
  const owned = new Set<Vertical>();
  for (const t of treatments) {
    if (t.status === "active" || t.status === "pending_review" || t.status === "paused") {
      const v = verticalOfMedication(t.medication);
      if (v) owned.add(v);
    }
  }

  // Rank candidate verticals by every owned vertical's complement list.
  const score = new Map<Vertical, number>();
  for (const ownedV of owned) {
    COMPLEMENTS[ownedV].forEach((v, i) => {
      if (owned.has(v)) return;
      score.set(v, (score.get(v) ?? 0) + (COMPLEMENTS[ownedV].length - i));
    });
  }

  // Motivation nudges from the latest WL intake (energy → longevity, etc).
  const latestWL = assessments.find((a) => a.treatment_type === "wl");
  const motivations = ((latestWL?.responses as { motivation?: string[] })?.motivation ?? [])
    .map((m) => m.toLowerCase());
  if (motivations.some((m) => m.includes("energy") || m.includes("confidence"))) {
    score.set("longevity", (score.get("longevity") ?? 0) + 2);
  }

  // No treatments yet → default entry points.
  const rankedVerticals: Vertical[] =
    score.size > 0
      ? Array.from(score.entries()).sort((a, b) => b[1] - a[1]).map(([v]) => v)
      : (["weight-loss", "trt", "longevity"] as Vertical[]);

  const out: ShopProduct[] = [];
  for (const v of rankedVerticals) {
    for (const p of CATALOG.filter((c) => c.vertical === v)) {
      if (out.length >= limit) return out;
      out.push(p);
      break; // one product per vertical keeps the bar diverse
    }
  }
  // Backfill if we still have room (e.g. only one candidate vertical).
  for (const p of CATALOG) {
    if (out.length >= limit) break;
    if (!out.includes(p) && !owned.has(p.vertical)) out.push(p);
  }
  return out.slice(0, limit);
}
